import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { scores, Score, categoryLabels, difficultyLabels } from '../../mocks/scores';
import ScoreCard from './components/ScoreCard';
import ScoreModal from './components/ScoreModal';
import JsonLd from '../../components/feature/JsonLd';

// ... existing code ...

const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

type CategoryFilter = 'all' | 'solo' | 'duo' | 'ensemble' | 'full';
type DifficultyFilter = 'all' | 'beginner' | 'intermediate' | 'advanced';

export default function ScoresPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [difficultyFilter, setDifficultyFilter] = useState<DifficultyFilter>('all');
  const [selectedScore, setSelectedScore] = useState<Score | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  const scoresJsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/scores#webpage`,
        url: `${siteUrl}/scores`,
        name: '楽譜販売 | 日本音楽藝術表現研究所',
        description:
          '日本音楽藝術表現研究所のオリジナル楽譜をデジタル販売。篠笛・太鼓・箏・三味線などの伝統楽器アンサンブル楽譜。中級・上級者向け作品。',
        isPartOf: { '@id': `${siteUrl}/#website` },
        inLanguage: 'ja',
      },
      {
        '@type': 'ItemList',
        name: '楽譜一覧',
        description: '日本音楽藝術表現研究所が開発した伝統楽器アンサンブルのオリジナル楽譜',
        numberOfItems: scores.length,
        itemListElement: scores.map((score, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Product',
            '@id': `${siteUrl}/scores#${score.id}`,
            name: score.title,
            description: score.description,
            url: `${siteUrl}/scores`,
            offers: {
              '@type': 'Offer',
              price: score.price,
              priceCurrency: 'JPY',
              availability: 'https://schema.org/InStock',
              url: `${siteUrl}/scores`,
            },
          },
        })),
      },
    ],
  }), []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.05 }
    );
    if (gridRef.current) observer.observe(gridRef.current);
    return () => observer.disconnect();
  }, []);

  const filtered = scores.filter((s) => {
    const catMatch = categoryFilter === 'all' || s.category === categoryFilter;
    const diffMatch = difficultyFilter === 'all' || s.difficulty === difficultyFilter;
    return catMatch && diffMatch;
  });

  const categories: CategoryFilter[] = ['all', 'solo', 'duo', 'ensemble', 'full'];
  const difficulties: DifficultyFilter[] = ['all', 'beginner', 'intermediate', 'advanced'];

  const difficultyAllLabel = 'すべて';

  return (
    <div className="bg-white min-h-screen">
      <title>楽譜販売 | Sheet Music Store | 日本音楽藝術表現研究所</title>
      <meta
        name="description"
        content="日本音楽藝術表現研究所のオリジナル楽譜をデジタル販売。篠笛・太鼓・箏・三味線などの伝統楽器アンサンブル楽譜。PDF形式、高解像度印刷対応。"
      />
      <meta name="keywords" content="楽譜販売,日本音楽,アンサンブル楽譜,篠笛,太鼓,箏,伝統音楽" />
      <link rel="canonical" href={`${siteUrl}/scores`} />
      <meta property="og:url" content={`${siteUrl}/scores`} />
      <meta property="og:title" content="楽譜販売 | Sheet Music Store | 日本音楽藝術表現研究所" />
      <meta
        property="og:description"
        content="日本音楽藝術表現研究所のオリジナル楽譜をデジタル販売。篠笛・太鼓・箏などの伝統楽器アンサンブル楽譜。"
      />
      <JsonLd data={scoresJsonLd} id="scores" />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="px-4 md:px-8 pt-4 pb-2 md:pt-5 md:pb-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="https://public.readdy.ai/ai/img_res/79998c31-8dcb-4257-9950-200bb8348595.png"
                alt="Institute Logo"
                className="h-9 w-9 md:h-10 md:w-10 object-contain shrink-0"
              />
              <div className={`font-serif text-xs md:text-sm transition-colors whitespace-nowrap ${scrolled ? 'text-sumi' : 'text-white'}`}>
                日本音楽藝術表現研究所
              </div>
            </Link>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`md:hidden w-8 h-8 flex flex-col items-center justify-center gap-1.5 cursor-pointer shrink-0 ${scrolled ? 'text-sumi' : 'text-white'}`}
              aria-label="メニュー"
            >
              <span className={`block w-5 h-px bg-current transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
              <span className={`block w-5 h-px bg-current transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`block w-5 h-px bg-current transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
            </button>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-8 mt-0">
              <Link to="/" className={`font-sans text-sm tracking-wide transition-colors whitespace-nowrap cursor-pointer hover:opacity-70 ${scrolled ? 'text-sumi' : 'text-white'}`}>
                ホーム
              </Link>
              <Link to="/en" className={`font-sans text-sm tracking-wide transition-colors whitespace-nowrap cursor-pointer hover:opacity-70 ${scrolled ? 'text-sumi' : 'text-white'}`}>
                EN
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <div className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)}></div>
        <div className={`absolute top-0 right-0 h-full w-64 bg-white shadow-xl transition-transform duration-300 ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="pt-20 px-8 flex flex-col gap-6">
            <Link to="/" onClick={() => setMenuOpen(false)} className="font-sans text-sm tracking-wide text-sumi cursor-pointer hover:opacity-60 transition-opacity whitespace-nowrap">
              ホーム
            </Link>
            <Link to="/en" onClick={() => setMenuOpen(false)} className="font-sans text-sm tracking-wide text-sumi cursor-pointer hover:opacity-60 transition-opacity whitespace-nowrap">
              EN
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative h-[60vh] md:h-[70vh] overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/ec0c6bc24f283ca47d6612907c33ef39.jpg"
          alt="楽譜販売"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-sumi/50 via-sumi/30 to-sumi/60"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <p className="font-sans text-xs tracking-[0.3em] text-white/60 uppercase mb-4">
            Sheet Music Store
          </p>
          <h1 className="font-serif text-4xl md:text-6xl text-white mb-4 leading-tight">
            楽譜販売
          </h1>
          <p className="font-serif text-sm md:text-base text-white/70 leading-relaxed max-w-md">
            研究所のコンセプトを基盤にしたオリジナル楽曲の販売
          </p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="sticky top-[60px] z-30 bg-white border-b border-sumi/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
            {/* Category Filter */}
            <div className="flex items-center gap-1 flex-wrap">
              <span className="font-sans text-xs text-sumi/40 mr-2 whitespace-nowrap">編成</span>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  className={`font-sans text-xs px-3 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    categoryFilter === cat
                      ? 'bg-sumi text-white'
                      : 'bg-washi text-sumi/60 hover:bg-sumi/10'
                  }`}
                >
                  {cat === 'all' ? 'すべて' : categoryLabels[cat]}
                </button>
              ))}
            </div>

            {/* Difficulty Filter */}
            <div className="flex items-center gap-1 flex-wrap">
              <span className="font-sans text-xs text-sumi/40 mr-2 whitespace-nowrap">難易度</span>
              {difficulties.map((diff) => (
                <button
                  key={diff}
                  onClick={() => setDifficultyFilter(diff)}
                  className={`font-sans text-xs px-3 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    difficultyFilter === diff
                      ? 'bg-sumi text-white'
                      : 'bg-washi text-sumi/60 hover:bg-sumi/10'
                  }`}
                >
                  {diff === 'all' ? difficultyAllLabel : difficultyLabels[diff]}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Product Grid */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16" ref={gridRef}>
        {/* Count */}
        <div className="mb-8 flex items-center justify-between">
          <p className="font-sans text-sm text-sumi/50">
            {filtered.length}件の楽譜
          </p>
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <i className="ri-music-2-line text-4xl text-sumi/20"></i>
            <p className="font-serif text-sm text-sumi/40">該当する楽譜がありません</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {filtered.map((score, i) => (
              <ScoreCard
                key={score.id}
                score={score}
                onClick={setSelectedScore}
                index={i}
                isVisible={isVisible}
              />
            ))}
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="bg-washi py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-sumi mb-6">ご購入について</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
            {[
              { icon: 'ri-file-pdf-line', title: 'PDF形式', desc: '購入後すぐにダウンロード可能。高解像度PDFで印刷にも対応。' },
              { icon: 'ri-mail-send-line', title: 'メール納品', desc: 'お問い合わせ確認後、登録メールアドレスへファイルをお送りします。' },
              { icon: 'ri-lock-line', title: '個人使用限定', desc: '購入した楽譜の複製・再配布は禁止されています。' },
            ].map((item) => (
              <div key={item.title} className="flex flex-col items-center gap-3 p-6 bg-white rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className={`${item.icon} text-2xl text-sumi/60`}></i>
                </div>
                <h3 className="font-serif text-base text-sumi">{item.title}</h3>
                <p className="font-sans text-xs text-sumi/60 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-sumi py-12 px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="https://public.readdy.ai/ai/img_res/79998c31-8dcb-4257-9950-200bb8348595.png"
              alt="Logo"
              className="h-8 w-8 object-contain"
            />
            <span className="font-serif text-xs text-white/80">日本音楽藝術表現研究所</span>
          </div>
          <p className="font-sans text-xs text-white/40">
            © 2025 Institute of Japanese Musical Art Expression. All rights reserved.
          </p>
          <Link to="/" className="font-sans text-xs text-white/50 hover:text-white/80 transition-colors cursor-pointer whitespace-nowrap">
            ← ホームへ戻る
          </Link>
        </div>
      </footer>

      {/* Modal */}
      <ScoreModal score={selectedScore} onClose={() => setSelectedScore(null)} />
    </div>
  );
}
